import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { format } from 'date-fns';

const Auctions: React.FC = () => {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'live' | 'ended'>('all');

  useEffect(() => {
    loadAuctions();
  }, []);

  const loadAuctions = async () => {
    try {
      const auctionsRef = collection(db, 'auctions');
      const q = query(auctionsRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      
      const auctionsList: Auction[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        auctionsList.push({
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Auction);
      });

      setAuctions(auctionsList);
    } catch (error) {
      console.error('Error loading auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (auction: Auction) => {
    const now = new Date();
    const start = new Date(auction.startDate);
    const end = new Date(auction.endDate);

    if (now < start) {
      return <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">Upcoming</span>;
    } else if (now >= start && now <= end) {
      return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Live</span>;
    } else {
      return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">Ended</span>;
    }
  };

  const filteredAuctions = auctions.filter(auction => {
    if (filter === 'all') return true;
    
    const now = new Date();
    const start = new Date(auction.startDate);
    const end = new Date(auction.endDate);

    if (filter === 'upcoming') return now < start;
    if (filter === 'live') return now >= start && now <= end;
    if (filter === 'ended') return now > end;
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading auctions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Auctions</h1>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-2 border-b">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 font-semibold ${
            filter === 'all'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          All Auctions ({auctions.length})
        </button>
        <button
          onClick={() => setFilter('upcoming')}
          className={`px-4 py-2 font-semibold ${
            filter === 'upcoming'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Upcoming
        </button>
        <button
          onClick={() => setFilter('live')}
          className={`px-4 py-2 font-semibold ${
            filter === 'live'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Live
        </button>
        <button
          onClick={() => setFilter('ended')}
          className={`px-4 py-2 font-semibold ${
            filter === 'ended'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Ended
        </button>
      </div>

      {/* Auctions Grid */}
      {filteredAuctions.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 text-lg">No auctions found</p>
          <p className="text-gray-500 mt-2">Check back later for upcoming auctions!</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAuctions.map((auction) => (
            <Link
              key={auction.id}
              to={`/auctions/${auction.id}`}
              className="card hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-1">{auction.title}</h3>
                  <p className="text-sm text-gray-600">{auction.auctionHouseName}</p>
                </div>
                {getStatusBadge(auction)}
              </div>

              {auction.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {auction.description}
                </p>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-700">
                  <span className="font-semibold mr-2">📦</span>
                  {auction.totalLots} Lots
                </div>
                <div className="flex items-center text-gray-700">
                  <span className="font-semibold mr-2">📅</span>
                  {format(new Date(auction.startDate), 'MMM dd, yyyy')}
                </div>
                <div className="flex items-center text-gray-700">
                  <span className="font-semibold mr-2">⏰</span>
                  {format(new Date(auction.startDate), 'h:mm a')} - {format(new Date(auction.endDate), 'h:mm a')}
                </div>
                {auction.location && (
                  <div className="flex items-center text-gray-700">
                    <span className="font-semibold mr-2">📍</span>
                    {auction.location}
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t">
                <button className="w-full btn-primary text-center">
                  View Lots & Predict
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Auctions;